<template>
    <layout>
        <a-breadcrumb>
            <a-breadcrumb-item data-cy="breadcrumb-main">
                {{ $t('globals.navigation.header.association_edit.title') }}
            </a-breadcrumb-item>
            <a-breadcrumb-item data-cy="breadcrumb-sub">
                {{ tabTitle(this.default_tab) }}
            </a-breadcrumb-item>
        </a-breadcrumb>

        <div class="shadow-xl settings">
            <a-tabs
                default-active-key="general"
                :active-key="default_tab"
                tab-position="left"
                size="default"
                @change="onTabChange"
                data-cy="tab-container"
            >
                <a-tab-pane key="main" :tab="tabTitle('main')" disabled />
                <a-tab-pane key="general" :tab="tabTitle('general')">
                    <tab-general
                        :association="association"
                        :association_id="association_id"
                        @is-form-dirty="isFormDirty"
                    />
                </a-tab-pane>
                <a-tab-pane key="companies" :tab="tabTitle('companies')">
                    <tab-companies
                        :association_name="association.name"
                        :association_id="association_id"
                        :association_claimsvault_module="association.is_module_claimsvault_enabled"
                        :association_covervault_module="association.is_module_covervault_enabled"
                        ref="CompaniesTab"
                    />
                </a-tab-pane>
                <a-tab-pane key="claims" :tab="tabTitle('claims')">
                    <tab-claims :association_id="association_id" ref="ClaimsTab" />
                </a-tab-pane>
                <a-tab-pane key="claimants" :tab="tabTitle('claimants')">
                    <tab-claimants :association_id="association_id" ref="ClaimantsTab" />
                </a-tab-pane>
                <a-tab-pane key="covers" v-if="isCoverVaultEnabled" :tab="tabTitle('covers')">
                    <tab-covers :association_id="association_id" ref="CoversTab" />
                </a-tab-pane>
                <a-tab-pane key="users" :tab="tabTitle('users')">
                    <tab-users :association_id="association_id" ref="UsersTab" />
                </a-tab-pane>
                <a-tab-pane
                    key="upload-history-claims"
                    :tab="tabTitle('upload-history-claims')"
                    v-if="isClaimsVaultEnabled"
                >
                    <tab-upload-history-claims :association_id="association_id" ref="UploadHistoryClaimsTab" />
                </a-tab-pane>
                <a-tab-pane
                    key="upload-history-covers"
                    :tab="tabTitle('upload-history-covers')"
                    v-if="isCoverVaultEnabled"
                >
                    <tab-upload-history-covers :association_id="association_id" ref="UploadHistoryCoversTab" />
                </a-tab-pane>
                <a-tab-pane key="active-sessions" :tab="tabTitle('active-sessions')">
                    <tab-active-sessions :association_id="association_id" ref="ActiveSessionsTab" />
                </a-tab-pane>
                <a-tab-pane key="audit-logs" :tab="tabTitle('audit-logs')">
                    <tab-audit-logs :association_id="association_id" ref="AuditLogsTab" />
                </a-tab-pane>
                <a-tab-pane key="intermediaries" v-if="isCiaEnabled" :tab="tabTitle('intermediaries')">
                    <tab-intermediaries ref="IntermediariesTab" :section_type="'association_admin'" />
                </a-tab-pane>
            </a-tabs>
        </div>
    </layout>
</template>

<script>
import Layout from '@/layouts/Layout.vue';
import TabGeneral from '@/components/tabs/association/General.vue';
import TabCovers from '@/components/tabs/Covers.vue';
import TabClaims from '@/components/tabs/Claims.vue';
import TabClaimants from '@/components/tabs/Claimants.vue';
import TabCompanies from '@/components/tabs/association/Companies.vue';
import TabUploadHistoryClaims from '@/components/tabs/association/UploadHistoryClaims.vue';
import TabUploadHistoryCovers from '@/components/tabs/association/UploadHistoryCovers.vue';
import TabUsers from '@/components/tabs/association/Users.vue';
import TabActiveSessions from '@/components/tabs/association/ActiveSessions.vue';
import TabAuditLogs from '@/components/tabs/association/AuditLogs.vue';
import TabIntermediaries from '@/components/tabs/Intermediaries.vue';

export default {
    components: {
        Layout,
        TabGeneral,
        TabCovers,
        TabClaims,
        TabClaimants,
        TabCompanies,
        TabUploadHistoryClaims,
        TabUploadHistoryCovers,
        TabUsers,
        TabActiveSessions,
        TabAuditLogs,
        TabIntermediaries,
    },
    data() {
        return {
            association_id: this.$route.params.id,
            association: {},
            default_tab: 'general',
            form_dirty: false,
        };
    },
    mounted() {
        const tab = this.$route.params.tab;
        this.fetch();
        this.setTab(tab);
    },
    computed: {
        isClaimsVaultEnabled() {
            return this.$store.state.user.modules.claimsvault;
        },
        isCoverVaultEnabled() {
            return this.$store.state.user.modules.covervault;
        },
        isCiaEnabled() {
            if (this.default_tab == 'intermediaries' || this.default_tab == 'templates') {
                if (!this.$store.state.user.modules.cia) {
                    this.onTabChange('general');
                }
            }

            if (this.default_tab == 'upload-history-claims') {
                if (!this.isClaimsVaultEnabled) {
                    this.onTabChange('general');
                }
            }

            if (this.default_tab == 'upload-history-covers') {
                if (!this.isCoverVaultEnabled) {
                    this.onTabChange('general');
                }
            }

            return this.$store.state.user.modules.cia;
        },
        isCoverVaultEnabled() {
            if (this.default_tab == 'covers' || this.default_tab == 'templates') {
                if (!this.$store.state.user.modules.covervault) {
                    this.onTabChange('general');
                }
            }

            return this.$store.state.user.modules.covervault;
        },
    },
    beforeRouteLeave(to, from) {
        let is_edit = this.$refs.IntermediariesTab == undefined ? false : this.$refs.IntermediariesTab.is_edit;

        if (this.form_dirty || is_edit) {
            return new Promise((resolve) => {
                this.$confirm({
                    title: this.$t('association.nav_modal.title'),
                    content: this.$t('association.nav_modal.content'),
                    onOk() {
                        resolve(true);
                    },
                    onCancel() {
                        resolve(false);
                    },
                });
            });
        }
    },
    methods: {
        setTab(tab) {
            switch (tab) {
                case 'companies':
                    if (typeof this.$refs.CompaniesTab !== 'undefined') {
                        this.$refs.CompaniesTab.reload();
                    }
                    this.default_tab = 'companies';
                    break;
                case 'covers':
                    if (typeof this.$refs.CoversTab !== 'undefined') {
                        this.$refs.CoversTab.reload();
                    }
                    this.default_tab = 'covers';
                    break;
                case 'claims':
                    if (typeof this.$refs.ClaimsTab !== 'undefined') {
                        this.$refs.ClaimsTab.reload();
                    }
                    this.default_tab = 'claims';
                    break;
                case 'claimants':
                    if (typeof this.$refs.ClaimantsTab !== 'undefined') {
                        this.$refs.ClaimantsTab.reload();
                    }
                    this.default_tab = 'claimants';
                    break;
                case 'users':
                    if (typeof this.$refs.UsersTab !== 'undefined') {
                        this.$refs.UsersTab.reload();
                    }
                    this.default_tab = 'users';
                    break;
                case 'active-sessions':
                    if (typeof this.$refs.ActiveSessionsTab !== 'undefined') {
                        this.$refs.ActiveSessionsTab.reload();
                    }
                    this.default_tab = 'active-sessions';
                    break;
                case 'upload-history-claims':
                    if (typeof this.$refs.UploadHistoryClaimsTab !== 'undefined') {
                        this.$refs.UploadHistoryClaimsTab.reload();
                    }
                    this.default_tab = 'upload-history-claims';
                    break;
                case 'upload-history-covers':
                    if (typeof this.$refs.UploadHistoryCoversTab !== 'undefined') {
                        this.$refs.UploadHistoryCoversTab.reload();
                    }
                    this.default_tab = 'upload-history-covers';
                    break;
                case 'audit-logs':
                    if (typeof this.$refs.AuditLogsTab !== 'undefined') {
                        this.$refs.AuditLogsTab.reload();
                    }
                    this.default_tab = 'audit-logs';
                    break;
                case 'intermediaries':
                    if (typeof this.$refs.IntermediariesTab !== 'undefined') {
                        this.$refs.IntermediariesTab.reload();
                    }
                    this.default_tab = 'intermediaries';
                    break;
                default:
                    this.default_tab = 'general';
                    break;
            }
        },
        tabTitle(key) {
            switch (key) {
                case 'main':
                    return this.$t('association.tabs.main');
                case 'general':
                    return this.$t('association.tabs.general');
                case 'companies':
                    return this.$t('association.tabs.companies');
                case 'covers':
                    return this.$t('association.tabs.covers');
                case 'claims':
                    return this.$t('association.tabs.claims');
                case 'claimants':
                    return this.$t('association.tabs.claimants');
                case 'upload-history-claims':
                    return this.$t('association.tabs.upload_history_claims');
                case 'upload-history-covers':
                    return this.$t('association.tabs.upload_history_covers');
                case 'users':
                    return this.$t('association.tabs.users');
                case 'active-sessions':
                    return this.$t('association.tabs.active_sessions');
                case 'audit-logs':
                    return this.$t('company.tabs.audit_logs');
                case 'intermediaries':
                    return this.$t('company.tabs.intermediaries');
            }
        },
        onTabChange(key) {
            const vm = this;
            let is_edit = this.$refs.IntermediariesTab == undefined ? false : this.$refs.IntermediariesTab.is_edit;

            if (this.form_dirty || is_edit) {
                this.$confirm({
                    title: this.$t('association.nav_modal.title'),
                    content: this.$t('association.nav_modal.content'),
                    onOk() {
                        vm.form_dirty = false;
                        vm.setTab(key);
                        vm.fetch();

                        if (is_edit) {
                            vm.$refs.IntermediariesTab.fetch();
                        }

                        vm.$router.push({
                            name: 'edit-associations-tab',
                            params: {
                                id: vm.association_id,
                                tab: key,
                            },
                        });
                    },
                    onCancel() {
                        vm.form_dirty = true;
                    },
                });
                this.form_dirty = vm.form_dirty;
            } else {
                this.setTab(key);
                this.fetch();

                this.$router.push({
                    name: 'edit-associations-tab',
                    params: {
                        id: this.association_id,
                        tab: key,
                    },
                });
            }
        },
        isFormDirty(is_dirty) {
            this.form_dirty = is_dirty;
        },
        fetch() {
            const id = this.$route.params.id;

            this.$http
                .get('/api/associations/' + id)
                .then((response) => {
                    let associationData = {
                        ...response.data.data,
                        password_include_numbers: response.data.data.password_include_numbers === 1 ? true : false,
                        password_include_symbols: response.data.data.password_include_symbols === 1 ? true : false,
                        is_ip_address_routing: response.data.data.is_ip_address_routing === 1 ? true : false,
                        is_hide_claim_description: response.data.data.is_hide_claim_description === 1 ? true : false,
                        is_enable_deactivation_process:
                            response.data.data.is_enable_deactivation_process == 1 ? true : false,
                        is_module_cia_enabled: response.data.data.is_module_cia_enabled === 1 ? true : false,
                        is_enforce_filename_restriction:
                            response.data.data.is_enforce_filename_restriction == 1 ? true : false,
                        is_allow_company_admin_assignment:
                            response.data.data.is_allow_company_admin_assignment == 1 ? true : false,
                        is_module_claimsvault_enabled:
                            response.data.data.is_module_claimsvault_enabled === 1 ? true : false,
                        is_module_covervault_enabled:
                            response.data.data.is_module_covervault_enabled === 1 ? true : false,
                        restrict_cover_dashboard_data:
                            response.data.data.restrict_cover_dashboard_data === 1 ? true : false,
                        import_date_limit:
                            response.data.data.import_date_limit === null
                                ? this.$dayjs()
                                : this.$dayjs(response.data.data.import_date_limit),
                        user_session_timeout: process.env.MIX_SESSION_LIFETIME,
                        admin_email:
                            response.data.data.admin_email === null || response.data.data.admin_email === ''
                                ? []
                                : _.split(response.data.data.admin_email, ','),
                        is_tfa_enabled: response.data.data.is_tfa_enabled === 1 ? true : false,
                        allow_dp_companies_to_add_api_users:
                            response.data.data.allow_dp_companies_to_add_api_users === 1 ? true : false,
                        enable_advanced_claims_analytics:
                            response.data.data.enable_advanced_claims_analytics === 1 ? true : false,
                        enable_advanced_cover_analytics:
                            response.data.data.enable_advanced_cover_analytics === 1 ? true : false,
                        is_claim_auto_removal_enabled:
                            response.data.data.is_claim_auto_removal_enabled === 1 ? true : false,
                        is_cover_auto_removal_enabled:
                            response.data.data.is_cover_auto_removal_enabled === 1 ? true : false,
                    };
                    this.association = associationData;
                })
                .catch((error) => {
                    if (error.response.status == 404) {
                        this.$router.push({
                            name: 'not-found',
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVAVW01' }),
                        });
                    }
                });
        },
    },
};
</script>
